import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { useLanguage } from "@/context/LanguageContext";
import { BookOpen, Calendar, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ExamHistory = () => {
  const { t, language } = useLanguage();
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/exam-history`);
      setAttempts(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const locale = language === "pt" ? "pt-BR" : language === "es" ? "es-ES" : "en-US";
    return date.toLocaleDateString(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Layout>
      <div className="p-6 md:p-12" data-testid="exam-history-page">
        {/* Header */}
        <div className="mb-12">
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
            {t("history")}
          </p>
          <h1 className="font-serif text-3xl md:text-4xl">
            {t("your_exams")}
          </h1>
        </div>

        {/* Stats Summary */}
        {!loading && attempts.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
            <div className="bento-item">
              <p className="stat-value">{attempts.length}</p>
              <p className="stat-label">{t("total_completed")}</p>
            </div>
            <div className="bento-item">
              <p className="stat-value">
                {Math.round(attempts.reduce((acc, a) => acc + a.score, 0) / attempts.length)}%
              </p>
              <p className="stat-label">{t("general_average")}</p>
            </div>
            <div className="bento-item hidden md:block">
              <p className="stat-value">
                {Math.max(...attempts.map(a => a.score))}%
              </p>
              <p className="stat-label">{t("best_score")}</p>
            </div>
          </div>
        )}

        {/* History List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-24 loading-skeleton" />
            ))}
          </div>
        ) : attempts.length > 0 ? (
          <div className="space-y-4">
            {attempts.map((attempt) => (
              <Link
                key={attempt.id}
                to={`/exams/${attempt.exam_id}/result/${attempt.id}`}
                className="block"
                data-testid={`history-item-${attempt.id}`}
              >
                <div className="bento-item flex items-center gap-4">
                  <div className="w-12 h-12 border flex items-center justify-center shrink-0">
                    <BookOpen size={20} strokeWidth={1} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-lg truncate">{attempt.exam_title}</h3>
                    <div className="flex items-center gap-4 text-xs font-mono text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {formatDate(attempt.completed_at)}
                      </span>
                      <span>
                        {attempt.correct_answers}/{attempt.total_questions} {t("correct_answers")}
                      </span>
                    </div>
                  </div>
                  
                  <div className={`text-2xl font-serif ${
                    attempt.score >= 70 ? "text-green-600" : 
                    attempt.score >= 50 ? "text-yellow-600" : "text-red-600"
                  }`}>
                    {attempt.score}%
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <BookOpen className="empty-state-icon" />
            <p className="empty-state-title">{t("no_exams_done")}</p>
            <p className="empty-state-description">
              {t("no_history_message")}
            </p>
            <Link to="/exams" className="mt-6 inline-block">
              <button className="btn-primary">{t("see_exams")}</button>
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ExamHistory;
